import { Modal } from '../Common/Modal';
import { SkeletonLoader } from '../Common/SkeletonLoader';
import FooterSkeleton from './FooterSkeleton';

/**
 * Boot-time skeleton for `OnePaymentView` (SINGLE_PAYMENT_METHOD compact).
 * Plain white background — we deliberately skip the publisher hero image
 * at skeleton time: it shifts in late, can fail to load on boot, and adds
 * an image fetch to the critical path. The real view paints the BG once
 * the rest of the data is ready.
 *
 *   ┌──────────────────────────┐
 *   │ centered max-w-[400px]   │
 *   │   profile + name + items-│
 *   │   toggle row             │
 *   ├──────────────────────────┤  divider
 *   │ centered max-w-[400px]   │
 *   │   wallet CTA placeholder │
 *   │   2-line EULA placeholder│
 *   ├──────────────────────────┤  footer divider
 *   │   powered-by + 3 links   │
 *   └──────────────────────────┘
 *
 * Items list is expanded by default on SINGLE, so we reserve a couple of
 * product rows plus a Total row to keep the boot→live transition from
 * jumping when the real `<DetailsWrapper/>` + `<TotalPrice/>` paint in.
 */
const OnePaymentSkeleton = () => {
  return (
    <Modal>
      <div
        data-testid="one-payment-skeleton"
        className="flex flex-col items-center justify-between bg-white w-full h-full pb-4"
      >
        <div className="flex flex-col items-center w-full shrink-0">
          {/* Reserve the same `relative h-5 w-full` slot the real
              `<XButton />` uses so the header lands at the same Y. */}
          <div className="h-5 w-full" />
          <div className="flex flex-col w-full max-w-[450px] md:max-w-[400px] px-4 mt-3 md:mt-10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <SkeletonLoader width="w-11" height="h-11" />
                <SkeletonLoader width="w-24" height="h-5" />
              </div>
              <div className="flex items-center gap-2">
                <SkeletonLoader width="w-12" height="h-3" />
                <SkeletonLoader width="w-3" height="h-3" />
              </div>
            </div>
            {/* Expanded items list + Total (details open by default on SINGLE). */}
            <div className="flex flex-col gap-3 mb-3">
              <div className="flex items-center justify-between">
                <SkeletonLoader width="w-32" height="h-4" />
                <SkeletonLoader width="w-12" height="h-4" />
              </div>
              <div className="flex items-center justify-between">
                <SkeletonLoader width="w-20" height="h-3" />
                <SkeletonLoader width="w-10" height="h-3" />
              </div>
              <div className="flex items-center justify-between">
                <SkeletonLoader width="w-16" height="h-5" />
                <SkeletonLoader width="w-14" height="h-5" />
              </div>
            </div>
            <div className="mt-3 border-t border-base-border" />
          </div>
        </div>

        <div className="flex flex-col w-full max-w-[450px] md:max-w-[400px] px-4 flex-1 gap-5 pt-6">
          <SkeletonLoader height="h-12" />
          <div className="flex flex-col items-center gap-2">
            <SkeletonLoader width="w-65" height="h-3" />
            <SkeletonLoader width="w-65" height="h-3" />
          </div>
        </div>

        <FooterSkeleton />
      </div>
    </Modal>
  );
};

export default OnePaymentSkeleton;
